import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { 
  Search, 
  Tag, 
  Calendar, 
  Clock, 
  User, 
  ArrowRight, 
  PlusCircle, 
  Lock, 
  Unlock,
  X, 
  Sparkles, 
  BookOpen, 
  Image as ImageIcon,
  CheckCircle2,
  AlertCircle,
  Edit3,
  Trash2,
  RotateCcw,
  LogOut,
  Eye
} from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { INITIAL_BLOGS } from '@/data/blogsData';

const DEFAULT_ADMIN_PASS = 'stellars2026';
const LOCAL_STORAGE_BLOGS = 'stellars_custom_blogs';
const ADMIN_AUTH_SESSION_KEY = 'stellars_admin_authenticated';

const BlogsPage = () => {
  const navigate = useNavigate();
  const [blogs, setBlogs] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTopic, setSelectedTopic] = useState('All');
  
  // Admin modal & Auth state
  const [isAdminModalOpen, setIsAdminModalOpen] = useState(false);
  const [adminPassword, setAdminPassword] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authError, setAuthError] = useState('');

  // Editing state
  const [editingBlogId, setEditingBlogId] = useState(null);

  // Blog Form state
  const [formData, setFormData] = useState({
    title: '',
    topic: 'Performance Marketing',
    category: 'Digital Strategy',
    author: 'Sahil Sharma',
    imageUrl: '',
    excerpt: '',
    keywords: '',
    content: ''
  });
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // Load blogs and check admin auth status from storage
  useEffect(() => {
    window.scrollTo(0, 0);

    const savedAuth = sessionStorage.getItem(ADMIN_AUTH_SESSION_KEY);
    if (savedAuth === 'true') {
      setIsAuthenticated(true);
    }

    try {
      const stored = localStorage.getItem(LOCAL_STORAGE_BLOGS);
      if (stored) {
        const parsed = JSON.parse(stored);
        setBlogs(parsed);
      } else {
        setBlogs(INITIAL_BLOGS);
        localStorage.setItem(LOCAL_STORAGE_BLOGS, JSON.stringify(INITIAL_BLOGS));
      }
    } catch (e) {
      console.error("Failed to load local blogs", e);
      setBlogs(INITIAL_BLOGS);
    }
  }, []);

  // Save blogs to localStorage helper
  const saveBlogsToStorage = (updatedBlogs) => {
    setBlogs(updatedBlogs);
    try {
      localStorage.setItem(LOCAL_STORAGE_BLOGS, JSON.stringify(updatedBlogs));
    } catch (err) {
      console.error("LocalStorage write error", err);
    }
  };

  // Topics for filtering
  const topics = ['All', ...new Set(blogs.map(b => b.topic))];

  const filteredBlogs = blogs.filter(blog => {
    const query = searchQuery.toLowerCase().trim();
    const matchesSearch = !query || 
      blog.title.toLowerCase().includes(query) ||
      blog.excerpt.toLowerCase().includes(query) ||
      (blog.keywords && blog.keywords.some(k => k.toLowerCase().includes(query))) ||
      blog.topic.toLowerCase().includes(query);
    
    const matchesTopic = selectedTopic === 'All' || blog.topic === selectedTopic;

    return matchesSearch && matchesTopic;
  });

  // Admin login
  const handleAdminAuth = (e) => {
    e.preventDefault();
    if (adminPassword === DEFAULT_ADMIN_PASS || adminPassword === 'admin123') {
      setIsAuthenticated(true);
      sessionStorage.setItem(ADMIN_AUTH_SESSION_KEY, 'true');
      setAuthError('');
      setAdminPassword('');
    } else {
      setAuthError('Incorrect Admin Password! (Hint: stellars2026)');
    }
  };

  // Admin logout
  const handleAdminLogout = () => {
    setIsAuthenticated(false);
    sessionStorage.removeItem(ADMIN_AUTH_SESSION_KEY);
    setIsAdminModalOpen(false);
    resetForm();
  };

  const resetForm = () => {
    setEditingBlogId(null);
    setFormData({
      title: '',
      topic: 'Performance Marketing',
      category: 'Digital Strategy',
      author: 'Sahil Sharma',
      imageUrl: '',
      excerpt: '',
      keywords: '',
      content: ''
    });
  };

  // Open modal for new blog
  const openNewBlogModal = () => {
    resetForm();
    setIsAdminModalOpen(true);
  };

  // Open modal to edit existing blog
  const handleEditBlog = (blog, e) => {
    if (e) e.stopPropagation();
    setEditingBlogId(blog.id);
    setFormData({
      title: blog.title,
      topic: blog.topic || 'Performance Marketing',
      category: blog.category || 'Digital Strategy',
      author: blog.author || 'Sahil Sharma',
      imageUrl: blog.imageUrl || '',
      excerpt: blog.excerpt || '',
      keywords: Array.isArray(blog.keywords) ? blog.keywords.join(', ') : (blog.keywords || ''),
      content: blog.content || ''
    });
    setIsAdminModalOpen(true);
  };

  // Delete blog handler
  const handleDeleteBlog = (blogId, blogTitle, e) => {
    if (e) e.stopPropagation();
    if (window.confirm(`Are you sure you want to delete "${blogTitle}"?`)) {
      const updated = blogs.filter(b => b.id !== blogId);
      saveBlogsToStorage(updated);
    }
  };

  // Reset to default seed blogs
  const handleResetToDefaults = () => {
    if (window.confirm("Reset all blog articles to default 1500+ word expert articles? Any custom edits will be reverted.")) {
      saveBlogsToStorage(INITIAL_BLOGS);
      alert("Blogs successfully reset to original state!");
    }
  };

  // Save (Create or Update) Blog
  const handleSaveBlog = (e) => {
    e.preventDefault();
    if (!formData.title.trim() || !formData.content.trim() || !formData.excerpt.trim()) {
      alert("Please fill in the Title, Initial Paragraph (Excerpt), and Full Content.");
      return;
    }

    const slug = formData.title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)+/g, '');

    const keywordsArray = formData.keywords
      ? formData.keywords.split(',').map(k => k.trim()).filter(Boolean)
      : ['Real Estate', 'Performance Marketing', 'AI Automation', 'Growth'];

    const wordCount = formData.content.trim().split(/\s+/).length;
    const calculatedReadTime = `${Math.max(5, Math.ceil(wordCount / 180))} min read`;

    const defaultImage = "https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=1200&q=80";

    if (editingBlogId) {
      // UPDATE EXISTING
      const updatedList = blogs.map(b => {
        if (b.id === editingBlogId) {
          return {
            ...b,
            title: formData.title,
            slug: slug || b.slug,
            topic: formData.topic,
            category: formData.category,
            author: formData.author || 'Sahil Sharma',
            imageUrl: formData.imageUrl.trim() || defaultImage,
            excerpt: formData.excerpt,
            keywords: keywordsArray,
            content: formData.content,
            readTime: calculatedReadTime
          };
        }
        return b;
      });
      saveBlogsToStorage(updatedList);
    } else {
      // CREATE NEW
      const newBlogEntry = {
        id: `blog-${Date.now()}`,
        slug: slug || `blog-${Date.now()}`,
        title: formData.title,
        topic: formData.topic || 'Performance Marketing',
        category: formData.category || 'Digital Strategy',
        author: formData.author || 'Sahil Sharma',
        date: new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }),
        readTime: calculatedReadTime,
        imageUrl: formData.imageUrl.trim() || defaultImage,
        excerpt: formData.excerpt,
        keywords: keywordsArray,
        content: formData.content
      };
      saveBlogsToStorage([newBlogEntry, ...blogs]);
    }

    setSubmitSuccess(true);
    setTimeout(() => {
      setSubmitSuccess(false);
      setIsAdminModalOpen(false);
      resetForm();
    }, 1200);
  };

  const currentWords = formData.content ? formData.content.trim().split(/\s+/).filter(Boolean).length : 0;

  return (
    <div className="min-h-screen bg-[#181F32] font-sans text-white">
      <Helmet>
        <title>Insights, Playbooks & Blogs | Stellars Estates</title>
        <meta name="description" content="Explore authoritative guides on luxury real estate lead generation, performance marketing, WhatsApp AI automation, and search engine dominance." />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Insights, Playbooks & Blogs | Stellars Estates" />
        <meta property="og:description" content="Explore authoritative guides on luxury real estate lead generation, performance marketing, WhatsApp AI automation, and search engine dominance." />
        <meta property="og:image" content="https://i.postimg.cc/3NNd39Q1/social-sharing-image-stallars-estates.jpg" />
        <meta property="og:image:secure_url" content="https://i.postimg.cc/3NNd39Q1/social-sharing-image-stallars-estates.jpg" />
        <meta property="og:image:alt" content="Stellars Estates - Marketing Insights & Playbooks" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://i.postimg.cc/3NNd39Q1/social-sharing-image-stallars-estates.jpg" />
      </Helmet>

      <Header />

      {/* Admin Mode Floating Banner if Authenticated */}
      {isAuthenticated && (
        <div className="bg-[#F3453F]/15 border-b border-[#F3453F]/40 py-2.5 px-4 sticky top-20 z-40 backdrop-blur-md">
          <div className="max-w-7xl mx-auto flex items-center justify-between text-xs sm:text-sm">
            <div className="flex items-center gap-2 text-white font-semibold">
              <Unlock size={16} className="text-[#F3453F]" />
              <span>Admin Mode Active: You can create, edit image URLs, or update any blog article.</span>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={openNewBlogModal}
                className="px-3 py-1 bg-[#F3453F] hover:bg-[#D93833] text-white rounded-lg font-bold text-xs flex items-center gap-1 shadow"
              >
                <PlusCircle size={14} />
                <span>+ Add Blog</span>
              </button>
              <button
                onClick={handleResetToDefaults}
                className="px-2.5 py-1 bg-white/10 hover:bg-white/20 text-white/80 rounded-lg text-xs flex items-center gap-1"
                title="Reset to 1500+ word defaults"
              >
                <RotateCcw size={12} />
                <span className="hidden sm:inline">Reset Defaults</span>
              </button>
              <button
                onClick={handleAdminLogout}
                className="px-2.5 py-1 bg-red-500/20 hover:bg-red-500/30 text-red-300 rounded-lg text-xs flex items-center gap-1 border border-red-500/30"
              >
                <LogOut size={12} />
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="bg-[#181F32] text-white pt-12 pb-16 sm:py-20 border-b border-white/10 relative overflow-hidden">
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#F3453F]/10 rounded-full blur-[140px] pointer-events-none"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl space-y-6">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#F3453F]/15 border border-[#F3453F]/30 text-[#F3453F] text-xs font-bold uppercase tracking-wider">
              <BookOpen size={14} />
              <span>Thought Leadership & Industry Playbooks</span>
            </div>

            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white font-heading leading-tight">
              Performance Marketing, AI Automation & <span className="text-[#F3453F]">Growth Playbooks</span>
            </h1>

            <p className="text-base sm:text-lg text-white/80 leading-relaxed font-sans">
              Authoritative, maximum-word deep dives into high-ticket real estate lead generation, autonomous WhatsApp AI workflows, SEO/AEO/GEO algorithms, and predictable enterprise growth.
            </p>

            <div className="pt-2 flex flex-wrap items-center gap-4">
              <button
                onClick={() => {
                  if (isAuthenticated) {
                    openNewBlogModal();
                  } else {
                    setIsAdminModalOpen(true);
                  }
                }}
                className="px-6 py-3.5 rounded-xl bg-[#F3453F] hover:bg-[#D93833] text-white font-bold text-xs sm:text-sm transition-all shadow-lg shadow-[#F3453F]/20 flex items-center gap-2"
              >
                {isAuthenticated ? <PlusCircle size={16} /> : <Lock size={16} />}
                <span>{isAuthenticated ? 'Create New Blog Article' : 'Admin: Add / Update Blog'}</span>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Search & Topic Filters */}
      <section className="py-6 bg-[#141A29] border-b border-white/10 sticky top-20 z-30 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-4">
          
          {/* Topic Pills */}
          <div className="flex items-center gap-2 overflow-x-auto w-full md:w-auto pb-2 md:pb-0 scrollbar-none">
            {topics.map(topic => (
              <button
                key={topic}
                onClick={() => setSelectedTopic(topic)}
                className={`px-4 py-2 rounded-xl text-xs font-bold whitespace-nowrap transition-all ${
                  selectedTopic === topic
                    ? 'bg-[#F3453F] text-white shadow-md'
                    : 'bg-[#181F32] text-white/70 hover:text-white border border-white/10'
                }`}
              >
                {topic}
              </button>
            ))}
          </div>

          {/* Search Box */}
          <div className="relative w-full md:w-80">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/40" size={16} />
            <input
              type="text"
              placeholder="Search topics, keywords..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-[#181F32] border border-white/10 text-white placeholder-white/40 text-xs font-sans focus:outline-none focus:border-[#F3453F]"
            />
          </div>

        </div>
      </section>

      {/* Blog Cards Grid */}
      <section className="py-16 sm:py-24 bg-[#181F32]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {filteredBlogs.length === 0 ? (
            <div className="text-center py-16 space-y-4 glass-panel bg-[#202942] p-8 rounded-2xl border border-white/15 max-w-lg mx-auto">
              <p className="text-base font-bold text-white/90">No articles found matching "{searchQuery}".</p>
              <button
                onClick={() => { setSearchQuery(''); setSelectedTopic('All'); }}
                className="px-5 py-2.5 text-xs font-bold bg-[#F3453F] text-white rounded-xl hover:bg-[#D93833] transition-all"
              >
                Clear Search Filters
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredBlogs.map((blog) => (
                <div 
                  key={blog.id} 
                  className="glass-panel bg-[#202942] rounded-2xl overflow-hidden border border-white/15 hover:border-[#F3453F]/60 transition-all duration-300 flex flex-col justify-between group shadow-xl relative"
                >
                  {/* Admin Quick Action Controls */}
                  {isAuthenticated && (
                    <div className="absolute top-3 right-3 z-20 flex items-center gap-1.5 bg-[#141A29]/90 backdrop-blur-md p-1.5 rounded-xl border border-white/20 shadow-lg">
                      <button
                        onClick={(e) => handleEditBlog(blog, e)}
                        className="p-1.5 rounded-lg bg-[#F3453F]/20 text-[#F3453F] hover:bg-[#F3453F] hover:text-white transition-all"
                        title="Edit / Update this Blog"
                      >
                        <Edit3 size={14} />
                      </button>
                      <button
                        onClick={(e) => handleDeleteBlog(blog.id, blog.title, e)}
                        className="p-1.5 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500 hover:text-white transition-all"
                        title="Delete this Blog"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  )}

                  <div className="space-y-4">
                    {/* Thumbnail Image */}
                    <div className="relative h-56 overflow-hidden bg-[#141A29]">
                      <img 
                        src={blog.imageUrl} 
                        alt={blog.title} 
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        onError={(e) => {
                          e.target.src = "https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=1200&q=80";
                        }}
                      />
                      <div className="absolute top-3 left-3 bg-[#181F32]/95 backdrop-blur-md px-3 py-1 rounded-full text-[10px] font-bold text-[#F3453F] border border-[#F3453F]/40 uppercase tracking-wider">
                        {blog.topic}
                      </div>
                    </div>

                    {/* Meta Details */}
                    <div className="px-6 space-y-3">
                      <div className="flex items-center gap-4 text-xs text-white/70 font-sans">
                        <span className="flex items-center gap-1.5"><Calendar size={13} className="text-[#F3453F]" /> {blog.date}</span>
                        <span className="flex items-center gap-1.5"><Clock size={13} className="text-[#F3453F]" /> {blog.readTime}</span>
                      </div>

                      {/* Title */}
                      <h2 className="text-xl font-bold text-white font-heading leading-snug group-hover:text-[#F3453F] transition-colors line-clamp-2">
                        {blog.title}
                      </h2>

                      {/* Initial Paragraph / Excerpt */}
                      <p className="text-xs sm:text-sm text-white/80 font-sans leading-relaxed line-clamp-3">
                        {blog.excerpt}
                      </p>

                      {/* Keywords Badges */}
                      <div className="flex flex-wrap gap-1.5 pt-2">
                        {blog.keywords && blog.keywords.slice(0, 3).map((kw, kIdx) => (
                          <span key={kIdx} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-white/10 text-[11px] font-medium text-white/85 border border-white/15">
                            <Tag size={10} className="text-[#F3453F]" />
                            {kw}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Read More Footer */}
                  <div className="p-6 pt-4 border-t border-white/10 mt-4 flex items-center justify-between">
                    <span className="text-xs font-semibold text-white/70 flex items-center gap-1.5">
                      <User size={13} className="text-[#F3453F]" />
                      {blog.author}
                    </span>
                    <Link
                      to={`/blog/${blog.slug}`}
                      className="inline-flex items-center gap-1.5 text-xs font-bold text-[#F3453F] hover:text-white hover:bg-[#F3453F] px-3.5 py-1.5 rounded-lg bg-[#F3453F]/10 border border-[#F3453F]/30 transition-all"
                    >
                      <span>Read Full Article</span>
                      <ArrowRight size={14} />
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}

        </div>
      </section>

      {/* Admin Add / Edit Blog Modal */}
      {isAdminModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/85 backdrop-blur-md overflow-y-auto">
          <div className="bg-[#181F32] border border-white/20 rounded-2xl max-w-3xl w-full p-6 sm:p-8 space-y-6 relative max-h-[92vh] overflow-y-auto shadow-2xl">
            
            <button
              onClick={() => setIsAdminModalOpen(false)}
              className="absolute top-4 right-4 p-2 text-white/60 hover:text-white rounded-lg bg-white/5"
            >
              <X size={20} />
            </button>

            {!isAuthenticated ? (
              /* Password Auth Step */
              <div className="space-y-6 py-4">
                <div className="text-center space-y-2">
                  <div className="w-14 h-14 rounded-full bg-[#F3453F]/15 text-[#F3453F] mx-auto flex items-center justify-center">
                    <Lock size={26} />
                  </div>
                  <h3 className="text-2xl font-bold text-white font-heading">Admin Blog Access</h3>
                  <p className="text-xs text-white/70 font-sans max-w-md mx-auto">
                    Enter the admin password to publish, update images via URL, and edit 1500+ word articles.
                  </p>
                  <p className="text-[11px] text-[#F3453F] font-mono bg-[#F3453F]/10 py-1 px-3 rounded-full inline-block">
                    Default Password: <strong>stellars2026</strong>
                  </p>
                </div>

                <form onSubmit={handleAdminAuth} className="space-y-4 max-w-sm mx-auto">
                  {authError && (
                    <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 text-xs flex items-center gap-2">
                      <AlertCircle size={14} />
                      <span>{authError}</span>
                    </div>
                  )}

                  <div>
                    <label className="block text-xs font-bold text-white/80 mb-1.5">Admin Password</label>
                    <input
                      type="password"
                      required
                      placeholder="Enter password..."
                      value={adminPassword}
                      onChange={(e) => setAdminPassword(e.target.value)}
                      className="w-full px-4 py-3 rounded-xl bg-[#141A29] border border-white/10 text-white placeholder-white/30 text-sm focus:outline-none focus:border-[#F3453F]"
                    />
                  </div>

                  <button
                    type="submit"
                    className="w-full py-3.5 rounded-xl bg-[#F3453F] hover:bg-[#D93833] text-white font-bold text-sm transition-all shadow-lg shadow-[#F3453F]/20"
                  >
                    Unlock Admin Access
                  </button>
                </form>
              </div>
            ) : (
              /* Create / Update Blog Form */
              <div className="space-y-6">
                <div className="flex items-center justify-between border-b border-white/10 pb-4">
                  <div className="space-y-1">
                    <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-green-500/15 text-green-400 text-[10px] font-bold">
                      <Sparkles size={12} />
                      <span>{editingBlogId ? 'Editing Blog Article' : 'Admin Publishing Mode'}</span>
                    </div>
                    <h3 className="text-2xl font-bold text-white font-heading">
                      {editingBlogId ? 'Update Existing Blog' : 'Publish New Blog Article'}
                    </h3>
                  </div>

                  <div className="text-right">
                    <span className="text-xs text-white/50 block">Word Count</span>
                    <span className={`text-sm font-bold font-mono ${currentWords >= 1200 ? 'text-green-400' : 'text-amber-400'}`}>
                      {currentWords} words
                    </span>
                  </div>
                </div>

                {submitSuccess ? (
                  <div className="p-8 rounded-xl bg-green-500/10 border border-green-500/30 text-center space-y-3">
                    <CheckCircle2 size={40} className="text-green-400 mx-auto" />
                    <h4 className="text-xl font-bold text-white">
                      {editingBlogId ? 'Blog Article Updated Successfully!' : 'New Blog Article Published Successfully!'}
                    </h4>
                    <p className="text-xs text-white/70">The changes have been saved to your site and are live now.</p>
                  </div>
                ) : (
                  <form onSubmit={handleSaveBlog} className="space-y-4 text-xs font-sans">
                    
                    {/* Title & Topic */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <div className="sm:col-span-2">
                        <label className="block font-bold text-white/80 mb-1">Article Title *</label>
                        <input
                          type="text"
                          required
                          placeholder="e.g. Scaling Performance Marketing & AI Lead Conversion in 2026"
                          value={formData.title}
                          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                          className="w-full px-3.5 py-2.5 rounded-xl bg-[#141A29] border border-white/10 text-white placeholder-white/30 text-xs focus:outline-none focus:border-[#F3453F]"
                        />
                      </div>

                      <div>
                        <label className="block font-bold text-white/80 mb-1">Topic / Category *</label>
                        <input
                          type="text"
                          required
                          placeholder="e.g. Performance Marketing"
                          value={formData.topic}
                          onChange={(e) => setFormData({ ...formData, topic: e.target.value })}
                          className="w-full px-3.5 py-2.5 rounded-xl bg-[#141A29] border border-white/10 text-white text-xs focus:outline-none focus:border-[#F3453F]"
                        />
                      </div>
                    </div>

                    {/* Image URL & Author */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-start">
                      <div className="sm:col-span-2 space-y-1">
                        <label className="block font-bold text-white/80">Cover Image URL *</label>
                        <div className="flex gap-2">
                          <input
                            type="url"
                            placeholder="https://images.unsplash.com/... or any image link"
                            value={formData.imageUrl}
                            onChange={(e) => setFormData({ ...formData, imageUrl: e.target.value })}
                            className="flex-1 px-3.5 py-2.5 rounded-xl bg-[#141A29] border border-white/10 text-white placeholder-white/30 text-xs focus:outline-none focus:border-[#F3453F]"
                          />
                        </div>
                        <p className="text-[10px] text-white/40">Paste any web image URL (Unsplash, CDN, or custom hosted image).</p>
                      </div>

                      <div>
                        <label className="block font-bold text-white/80 mb-1">Author Name</label>
                        <input
                          type="text"
                          placeholder="Sahil Sharma"
                          value={formData.author}
                          onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                          className="w-full px-3.5 py-2.5 rounded-xl bg-[#141A29] border border-white/10 text-white placeholder-white/30 text-xs focus:outline-none focus:border-[#F3453F]"
                        />
                      </div>
                    </div>

                    {/* Live Image Preview Thumbnail */}
                    {formData.imageUrl && (
                      <div className="p-3 rounded-xl bg-[#141A29] border border-white/10 flex items-center gap-4">
                        <img 
                          src={formData.imageUrl} 
                          alt="Cover preview" 
                          className="w-24 h-16 object-cover rounded-lg border border-white/10 shrink-0"
                          onError={(e) => { e.target.src = "https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=600&q=80"; }}
                        />
                        <div className="text-[11px] text-white/70">
                          <span className="font-bold text-white block">Image URL Preview</span>
                          <span className="truncate block max-w-md text-white/40">{formData.imageUrl}</span>
                        </div>
                      </div>
                    )}

                    {/* Keywords */}
                    <div>
                      <label className="block font-bold text-white/80 mb-1">Keywords / Entity Tags (comma separated)</label>
                      <input
                        type="text"
                        placeholder="Real Estate Marketing, Meta Ads, Google PPC, AI Automation, ROAS"
                        value={formData.keywords}
                        onChange={(e) => setFormData({ ...formData, keywords: e.target.value })}
                        className="w-full px-3.5 py-2.5 rounded-xl bg-[#141A29] border border-white/10 text-white placeholder-white/30 text-xs focus:outline-none focus:border-[#F3453F]"
                      />
                    </div>

                    {/* Initial Paragraph Excerpt */}
                    <div>
                      <label className="block font-bold text-white/80 mb-1">Initial Paragraph / Excerpt (Displayed on card) *</label>
                      <textarea
                        rows={3}
                        required
                        placeholder="Write a compelling 2-3 sentence overview that introduces the core topic..."
                        value={formData.excerpt}
                        onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                        className="w-full px-3.5 py-2.5 rounded-xl bg-[#141A29] border border-white/10 text-white placeholder-white/30 text-xs focus:outline-none focus:border-[#F3453F]"
                      />
                    </div>

                    {/* Full Content (Markdown) */}
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className="font-bold text-white/80">Full Article Content (Markdown format, 1500+ words recommended) *</label>
                        <span className="text-[10px] text-white/50">Supports # H1, ## H2, ### H3, tables, and lists</span>
                      </div>
                      <textarea
                        rows={10}
                        required
                        placeholder="# Main Heading&#10;&#10;Introduction paragraph...&#10;&#10;## 1. Subheading&#10;&#10;Detailed explanations and analysis..."
                        value={formData.content}
                        onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                        className="w-full px-3.5 py-2.5 rounded-xl bg-[#141A29] border border-white/10 text-white placeholder-white/30 text-xs font-mono focus:outline-none focus:border-[#F3453F] leading-relaxed"
                      />
                    </div>

                    {/* Action buttons */}
                    <div className="flex items-center justify-end gap-3 pt-2">
                      <button
                        type="button"
                        onClick={() => setIsAdminModalOpen(false)}
                        className="px-4 py-2.5 rounded-xl bg-white/10 hover:bg-white/20 text-white font-bold text-xs transition-all"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="px-6 py-2.5 rounded-xl bg-[#F3453F] hover:bg-[#D93833] text-white font-bold text-xs sm:text-sm transition-all shadow-lg shadow-[#F3453F]/20"
                      >
                        {editingBlogId ? 'Save & Update Article' : 'Publish Blog Article'}
                      </button>
                    </div>

                  </form>
                )}
              </div>
            )}

          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default BlogsPage;
